import { useState } from "react";
import { MapPin, Mail, Send, Check } from "lucide-react";

const offices = [
  {
    city: "Singapore (HQ)",
    company: "Technofocus Pte. Ltd.",
    address: "138 Robinson Road, #26-01 Oxley Tower, Singapore 068906",
    email: "contact@technofocus.ai",
    image: "https://images.pexels.com/photos/35546349/pexels-photo-35546349.jpeg",
  },
  {
    city: "New Delhi, India",
    company: "Technofocus Solutions Pvt. Ltd.",
    address: "K-1, Green Park, New Delhi-110016",
    email: "contact@technofocus.ai",
    image: "https://images.pexels.com/photos/31713110/pexels-photo-31713110.jpeg",
  },
];

const contactReasons = [
  "Course enquiry",
  "Corporate training",
  "Platform demo (LevelUp / EventsUp / StepUp)",
  "Partner / Trainer enquiry",
  "General question",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", reason: "", message: "" });
  const [status, setStatus] = useState("idle");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus("success");
        setForm({ name: "", email: "", organization: "", reason: "", message: "" });
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="overflow-x-hidden">
      {/* ── HERO ── */}
      <section
        data-testid="contact-hero-section"
        className="pt-32 pb-20"
        style={{ background: "linear-gradient(135deg, #091F2C 0%, #194449 55%, #1E5C60 100%)" }}
      >
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-5" style={{ color: '#D4EC8E' }}>Get in Touch</p>
          <h1 className="text-4xl sm:text-5xl font-light text-white tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Let's Start a<br />
            <span style={{ color: '#D4EC8E', fontWeight: 600 }}>Conversation</span>
          </h1>
          <p className="text-lg" style={{ color: 'rgba(185,220,210,0.85)' }}>
            Have a question about our training programs, platforms, or partnership opportunities? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* ── FORM + CONTACT ── */}
      <section data-testid="contact-main-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-14">
            {/* Contact form */}
            <div className="lg:col-span-3">
              <h2 className="text-2xl font-semibold text-gray-900 mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>Send us a Message</h2>

              {status === "success" ? (
                <div data-testid="contact-success" className="flex flex-col items-center justify-center gap-4 py-16 rounded-2xl border" style={{ backgroundColor: '#E5F3F0', borderColor: '#B9DCD2' }}>
                  <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: '#194449' }}>
                    <Check className="text-white" size={24} />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg">Message Sent!</h3>
                  <p className="text-gray-500 text-center max-w-xs">Thanks for reaching out. Our team will get back to you within 1–2 business days.</p>
                  <button onClick={() => setStatus("idle")} className="text-sm font-medium hover:underline" style={{ color: '#194449' }}>Send another message</button>
                </div>
              ) : (
                <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="Your full name"
                        data-testid="contact-name-input"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:border-transparent text-sm transition-all"
                        style={{ '--tw-ring-color': '#194449' }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="you@organization.com"
                        data-testid="contact-email-input"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:border-transparent text-sm transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Organization</label>
                    <input
                      type="text"
                      name="organization"
                      value={form.organization}
                      onChange={handleChange}
                      placeholder="Your company or organization"
                      data-testid="contact-org-input"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:border-transparent text-sm transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">What can we help with?</label>
                    <select
                      name="reason"
                      value={form.reason}
                      onChange={handleChange}
                      data-testid="contact-reason-select"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:border-transparent text-sm transition-all bg-white"
                    >
                      <option value="">Select a reason</option>
                      {contactReasons.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Message *</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Tell us how we can help..."
                      data-testid="contact-message-input"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:border-transparent text-sm transition-all resize-none"
                    />
                  </div>
                  {status === "error" && (
                    <p data-testid="contact-error" className="text-red-500 text-sm">Something went wrong. Please email us directly at contact@technofocus.ai.</p>
                  )}
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    data-testid="contact-submit-button"
                    className="w-full flex items-center justify-center gap-2 text-white py-4 rounded-xl font-semibold transition-colors disabled:opacity-60"
                    style={{ backgroundColor: '#194449' }}
                    onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#0B2E38'; }}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#194449'}
                  >
                    {status === "loading" ? (
                      <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Sending...</span>
                    ) : (
                      <><Send size={16} /> Send Message</>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Info panel */}
            <div className="lg:col-span-2 space-y-6">
              <div className="rounded-2xl p-7 border" style={{ backgroundColor: '#FFF8F3', borderColor: '#E2EBE9' }}>
                <div className="flex items-center gap-3 mb-4">
                  <Mail size={18} style={{ color: '#194449' }} />
                  <h3 className="font-semibold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Email Us</h3>
                </div>
                <p className="text-gray-500 text-sm mb-3">For general enquiries:</p>
                <a href="mailto:contact@technofocus.ai" data-testid="contact-email-link" className="text-sm font-medium hover:underline" style={{ color: '#194449' }}>contact@technofocus.ai</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── OFFICES ── */}
      <section data-testid="contact-offices-section" className="py-20" style={{ backgroundColor: '#FFF8F3' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: '#194449' }}>Global Presence</p>
            <h2 className="text-3xl font-medium text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Our Offices</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {offices.map((office, i) => (
              <div key={i} data-testid={`office-detail-${i}`} className="bg-white rounded-2xl overflow-hidden border shadow-sm hover:shadow-lg transition-all" style={{ borderColor: '#E2EBE9' }}>
                <img src={office.image} alt={office.city} className="w-full object-cover" style={{ height: 170 }} />
                <div className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{office.city}</h3>
                  <p className="text-xs font-medium mb-3" style={{ color: '#194449' }}>{office.company}</p>
                  <div className="flex items-start gap-2 text-gray-500 text-sm mb-3">
                    <MapPin size={13} className="shrink-0 mt-0.5" style={{ color: '#194449' }} />
                    {office.address}
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail size={13} style={{ color: '#194449' }} />
                    <a href={`mailto:${office.email}`} className="text-sm hover:underline" style={{ color: '#194449' }}>{office.email}</a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
